/**
 * Experiment registry — PURE DATA, the single source of truth for experiment
 * keys, descriptions and option shapes. This is the file you edit to add or
 * change an experiment (see ./README.md).
 *
 * An experiment is registered here ONLY when product code actually reads it —
 * via `useExperiment(key)` (React) or `getExperiment(key)` (server). Registering
 * a flag that nothing reads surfaces it in the Flags Explorer toolbar as a
 * no-op override, which is misleading; keeping the registry honest means every
 * entry here flips something real.
 *
 * Deliberately has ZERO imports, and none may ever be added
 * (`__tests__/registry.spec.ts` enforces this): client bundles may read this
 * module, and any import here risks dragging the Node-only Flags-SDK wiring
 * (`internal/define.ts` → `flags/next` → `async_hooks`) into a browser bundle.
 *
 * Two entry shapes:
 * - `kind: 'variants'` — the runtime value is a variant-name STRING; carries
 *   `variants` (readonly tuple) + `defaultVariant`.
 * - `kind: 'param'` — the runtime value is a Statsig group PARAMETER OBJECT
 *   (e.g. `{ active: boolean }`); carries `options` ({ label, value }[]) +
 *   `defaultValue`. See `internal/define.ts` for why param experiments exist.
 *
 * `internal/experiments.ts` (the Node-only Flags-SDK wiring for the toolbar /
 * discovery endpoint) DERIVES its definitions from this file — one wiring
 * line per entry (type-check fails if you forget it). Add or edit
 * experiments HERE — never in both places.
 */
export type ExperimentRegistryEntry =
  | {
      kind: 'variants';
      key: string;
      description: string;
      variants: readonly string[];
      defaultVariant: string;
      /** Statsig unit the experiment randomises on. Omitted = 'stableID'
       *  (per browser). 'userID' = the customerId, 'personID' = the auth
       *  personId (`identifyCustomer` / `identifyPerson` set them) — the
       *  client re-evaluates these after identify; see README "User-unit
       *  experiments". Must match the Statsig console `idType`. */
      unit?: 'stableID' | 'userID' | 'personID';
    }
  | {
      kind: 'param';
      key: string;
      description: string;
      options: readonly { label: string; value: unknown }[];
      defaultValue: unknown;
      /** Statsig unit the experiment randomises on. Omitted = 'stableID'
       *  (per browser). 'userID' = the customerId, 'personID' = the auth
       *  personId (`identifyCustomer` / `identifyPerson` set them) — the
       *  client re-evaluates these after identify; see README "User-unit
       *  experiments". Must match the Statsig console `idType`. */
      unit?: 'stableID' | 'userID' | 'personID';
    };

export const experimentsRegistry = {
  'hero-cta-copy-v1': {
    kind: 'variants',
    key: 'hero-cta-copy-v1',
    description: 'Smoke-test: A/B test hero CTA button copy',
    variants: ['control', 'treatment-a'],
    defaultVariant: 'control',
  },
  // Param-object experiment (NOT variant-string): the arm lives in an `arm`
  // parameter, matching live Statsig serving (`value.arm`) and the shape
  // getPersonalisationConfig reads. Param form so a Flags Explorer override
  // writes `{ arm: 'treatment', layout: 'next' }` verbatim — a bare
  // variant-string override would read as `value.arm === undefined` and
  // silently fall back to control, blocking override-based QA.
  //
  // MVX-733: the NO restart, per customer. It replaces the per-browser
  // `personalisation-offer-ranking`, which could not switch unit in place.
  // Treatment carries the engine's sorting AND the new offers page together:
  // `layout: 'next'` is what `resolve-mypage-version` reads to pick the page
  // (specs/MVX-733-offers-page-arm-switch/spec.md §3.1–3.2). Which key a
  // session reads is decided by `personalisationExperimentKey(market)` in
  // `@axo/personalisation-engine/contracts`.
  'personalisation-offer-ranking-no': {
    kind: 'param',
    key: 'personalisation-offer-ranking-no',
    description:
      'NO (axofinans.no): reorder MyPage bank offers via the personalisation engine, on the new offers page (MVX-733)',
    options: [
      { label: 'Control', value: { arm: 'control' } },
      { label: 'Treatment', value: { arm: 'treatment', layout: 'next' } },
    ],
    defaultValue: { arm: 'control' },
    unit: 'userID',
  },
  // Per-MARKET sibling of `personalisation-offer-ranking-no` — same sorting, same
  // metrics, its own Statsig experiment so FI and NO run (and conclude)
  // independently rather than sharing one bucketing and one readout. Its
  // treatment has no `layout`, so FI keeps today's page. Which key a
  // session reads is decided by `personalisationExperimentKey(market)` in
  // `@axo/personalisation-engine/contracts`; the two are mutually
  // exclusive because their targeting gates are brand-disjoint.
  //
  // Onboarding a THIRD market means another entry here plus a branch there — the
  // map is deliberately explicit, so an unlisted market reads a key with no
  // targeting and nothing silently enrolls.
  'personalisation-offer-ranking-fi': {
    kind: 'param',
    key: 'personalisation-offer-ranking-fi',
    description:
      'FI (axolaina.fi): reorder MyPage bank offers + reassurance claim via the personalisation engine',
    options: [
      { label: 'Control', value: { arm: 'control' } },
      { label: 'Treatment', value: { arm: 'treatment' } },
    ],
    defaultValue: { arm: 'control' },
    // MV-0-017: one arm per customer (any device, ACE agents).
    unit: 'userID',
  },
  // Read server-side by site-axo `getContentExperiments` via `getExperiment`.
  // Param-object (not variant-string) so the toolbar override writes
  // `{ active: true }` — the shape `getExperiment<{active}>` branches on.
  'no-campaign-minimal': {
    kind: 'param',
    key: 'no-campaign-minimal',
    description:
      'Campaign-page minimal variant. Treatment ({ active: true }) activates the ' +
      'hero-minimal + preform-minimal content flags on /kampanje/forbrukslan and ' +
      '/kampanje/refinansiering (see site-axo getContentExperiments).',
    options: [
      { label: 'Control', value: { active: false } },
      { label: 'Treatment', value: { active: true } },
    ],
    defaultValue: { active: false },
  },
  // Round 2 of the smart-form-preform experiment (MV-3346): round 1
  // (`smart-form-preform`, MV-3104) concluded as Control and was removed
  // (MVX-612); it is terminal in Statsig, so this is a fresh experiment ID.
  // Read server-side by site-axo `getSecuredPreformVariant` via `getExperiment`.
  // Param-object (not variant-string) so the toolbar override writes
  // `{ active: true }` — the shape `getExperiment<{active}>` branches on.
  // Treatment ({ active: true }) renders the variant-B smart-form
  // review/amount step fixes: gates removed, debt sidebar,
  // loan-purpose/comment card.
  'smart-form-preform-v2': {
    kind: 'param',
    key: 'smart-form-preform-v2',
    description:
      'NO-only SECURED_LOAN hero swap, round 2 (MV-3346): treatment ({ active: true }) ' +
      'renders the variant-B SmartFormPreForm review/amount step (gates removed, debt ' +
      'sidebar, loan-purpose/comment card) instead of SecuredLoansPreForm on site-axo.',
    options: [
      { label: 'Control', value: { active: false } },
      { label: 'Treatment', value: { active: true } },
    ],
    defaultValue: { active: false },
  },
  // Param-object experiment (NOT variant-string): the arm lives in an `arm`
  // parameter, matching live Statsig serving (`value.arm`) and the shape
  // PersonalizedInsuranceCard reads. Param form so a Flags Explorer override
  // writes `{ arm: 'personalized' }` verbatim — a bare variant-string override
  // would read as `value.arm === undefined` and silently fall back to control,
  // blocking override-based QA on localhost/preview.
  'insurance-banner-title-se': {
    kind: 'param',
    key: 'insurance-banner-title-se',
    description:
      'SE-only: rules-personalized vs static insurance banner title on the MyPage offer list',
    options: [
      { label: 'Control', value: { arm: 'control' } },
      { label: 'Personalized', value: { arm: 'personalized' } },
    ],
    defaultValue: { arm: 'control' },
  },
  // Recommendation-footer tone (warm | confident | curated). NOT a live A/B yet:
  // product reads it via `flags().getFlag` / `useFlag` (NO exposure) with a static
  // default, so it ships `confident` and business can flip it in the Flags Explorer
  // (or via a Statsig serve) without a deploy. Registered as an experiment purely
  // so the toolbar shows a 3-way variant picker for the override. Promote to a
  // Statsig LAYER (allocation + `useExperiment` exposure) when a measured tone test
  // is wanted — that is the only step left for a real A/B.
  recommendation_tone: {
    kind: 'variants',
    key: 'recommendation_tone',
    description:
      'Tone of the MyPage recommendation footer copy (warm | confident | curated). Ships confident; flip in the Flags Explorer, no deploy.',
    variants: ['warm', 'confident', 'curated'],
    defaultVariant: 'confident',
  },
  'ipi-form-primer': {
    kind: 'param',
    key: 'ipi-form-primer',
    description:
      'In-form IPI warm-up primer in the one-form final step (control = nothing rendered). Exposure is scoped to insurance-eligible users only.',
    options: [
      { label: 'Control', value: { arm: 'control' } },
      { label: 'Treatment — primer', value: { arm: 'treatment' } },
    ],
    defaultValue: { arm: 'control' },
  },
  // Param-object experiment (NOT variant-string) — same rationale as
  // insurance-banner-title-se above. The `control` group MUST define
  // `{ arm: 'control' }` explicitly in Statsig: empty-value groups are
  // dropped by the middleware precompute (MV-3159).
  //
  // NO-only via a Statsig targeting gate (custom.country == 'NO'), mirroring
  // the SE experiment's targeting-gate pattern — no code-level country gate;
  // a non-enrolled read resolves the defaultValue and logs no exposure.
  'insurance-banner-status-no': {
    kind: 'param',
    key: 'insurance-banner-status-no',
    description:
      'NO-only: application-status-aware vs static insurance banner content on the MyPage offer list (MV-3164)',
    options: [
      { label: 'Control', value: { arm: 'control' } },
      { label: 'Status-aware', value: { arm: 'status-aware' } },
    ],
    defaultValue: { arm: 'control' },
  },
  // Param-object experiment (NOT variant-string): the arm lives in an `arm`
  // parameter, matching live Statsig serving (`value.arm`) and the shape
  // useArm reads. The `control` group MUST define `{ arm: 'control' }`
  // explicitly in Statsig: empty-value groups are dropped by the middleware
  // precompute (MV-3159).
  //
  // Market scope (NO, FI) is a Statsig targeting rule only; the code is
  // market-agnostic and `application.MarketCountry` picks the market in the
  // service. FI goes live only when the targeting gate adds it (MV-3781).
  'recommendation-content-decision-step': {
    kind: 'param',
    key: 'recommendation-content-decision-step',
    description:
      'PD-947: one-form decision step renders personalised recommendation-content layouts ' +
      '(treatment) vs the static ProductRecommendationCards (control). MyPage recommendations ' +
      'read the same arm (MV-3738). Any service failure renders control.',
    options: [
      { label: 'Control', value: { arm: 'control' } },
      { label: 'Treatment — content layouts', value: { arm: 'treatment' } },
    ],
    defaultValue: { arm: 'control' },
  },
  // Param-object experiment (NOT variant-string) — same rationale as the
  // entries above: a bare variant-string override would read as
  // `value.arm === undefined` and silently fall back to control, blocking
  // override-based QA. The `control` group MUST define `{ arm: 'control' }`
  // explicitly in Statsig: empty-value groups are dropped by the middleware
  // precompute and would never log exposure.
  //
  // MIGRATED from the LaunchDarkly flag of the same key (LD project `team-1`,
  // NOT `default`), which served these arms as bare strings. Exposure here is
  // scoped to the accept modal and fires ONLY for insurance-eligible users
  // accepting a LOAN offer — the read is mounted behind that gate in
  // `AcceptedOfferModalExperiment`, so users who cannot see either arm never
  // enter the population. MV-3485.
  //
  // NOT a like-for-like migration any more: LD served six NO brands, this adds
  // SE. Results are therefore NOT comparable with the 81 days of LD A/B/C data
  // (2026-06-19 to 09-08) — that window is NO-only. Market scope lives in the
  // `insurance-card-in-accept-modal-targeting` gate, not here.
  'insurance-card-in-accept-modal': {
    kind: 'param',
    key: 'insurance-card-in-accept-modal',
    description:
      'NO + SE: IPI insurance upsell in the MyPage offer-accept modal — control (plain AcceptedOfferModal) vs extended (AcceptedOfferIpiModal). Exposure scoped to insurance-eligible loan accepts (MV-3485).',
    options: [
      { label: 'Control', value: { arm: 'control' } },
      { label: 'Extended', value: { arm: 'extended' } },
    ],
    defaultValue: { arm: 'control' },
  },
  // Param-object experiment (NOT variant-string), same rationale as the entries
  // above. `{ arm }` and not `{ active }`: the event-log adapter's `deriveArm`
  // reads a string or an object's `.arm`, and anything else is JSON-stringified
  // into the Redshift `arm` column. The `control` group MUST define
  // `{ arm: 'control' }` explicitly in Statsig: empty-value groups are dropped
  // by the middleware precompute and would never log exposure (MV-3159).
  //
  // Exposure fires ONLY inside product-insurance's InsuranceFlowExperiment,
  // mounted for gate-passing, insurance-eligible Billogram visitors, verified
  // or not — the population that can see either arm. Market scope lives in
  // the `ipi-baseline-targeting` gate, not here. MV-3564 / MV-3565.
  'ipi-baseline': {
    kind: 'param',
    key: 'ipi-baseline',
    description:
      'NO + SE: the option-D baseline IPI page (combined intro + purchase, BankID/Vipps for visitors not yet verified, success screen, instant hand-off) vs the legacy widget flow, 50/50 (MV-3565).',
    options: [
      { label: 'Control', value: { arm: 'control' } },
      { label: 'Baseline', value: { arm: 'baseline' } },
    ],
    defaultValue: { arm: 'control' },
  },
  // Version C against the ipi-baseline winner (MV-3759). Same shape and the same
  // exposure point as `ipi-baseline`: product-insurance's boundary reads THIS key
  // instead of `ipi-baseline` while the `ipi-version-c-targeting` gate passes,
  // never both. At launch it has two groups: `{ arm: 'versionC' }` and a
  // comparison group serving the winner's own arm value, `control` or
  // `baseline`. That value picks the layout, which is how the test runs against
  // either winner without a release. All three values are listed so either
  // comparison, and a Flags Explorer override to any of them, is valid. Every
  // group defines its `{ arm }` explicitly (MV-3159). NO + SE via the gate.
  'ipi-version-c': {
    kind: 'param',
    key: 'ipi-version-c',
    description:
      'NO + SE: Version C of the IPI offer page (MV-3759) vs the ipi-baseline winner. Groups: { arm: "versionC" } and a comparison group serving the winner\'s arm, "control" or "baseline". Read instead of ipi-baseline while ipi-version-c-targeting passes. Metric: Insurance Policy Created.',
    options: [
      { label: 'Control (legacy widget)', value: { arm: 'control' } },
      { label: 'Baseline', value: { arm: 'baseline' } },
      { label: 'Version C', value: { arm: 'versionC' } },
    ],
    defaultValue: { arm: 'control' },
  },
  // The `control` group MUST define `{ arm: 'control' }` explicitly in Statsig:
  // empty-value groups are dropped by the middleware precompute and never log
  // exposure (MV-3159). NO-only via a Statsig targeting gate; no country
  // check in code.
  'no-mortgage-monthly-payment': {
    kind: 'param',
    key: 'no-mortgage-monthly-payment',
    description:
      'NO-only: ask homeowners for their monthly mortgage payment under Boliggjeld on the One Form debt step (treatment) vs not asking (control), 50/50. Primary: form completion (submitted ÷ exposed), ship unless treatment is > 1.5 pp worse; guardrail: homeowner approval rate (MV-3746).',
    options: [
      { label: 'Control', value: { arm: 'control' } },
      { label: 'Treatment — monthly payment', value: { arm: 'treatment' } },
    ],
    defaultValue: { arm: 'control' },
  },
  // Param-object experiment; the `control` group MUST define `{ arm: 'control' }`
  // explicitly in Statsig (MV-3159). User-unit: one arm per customer across the
  // decision step and MyPage. Exposure fires only where a SecuredLoan
  // recommendation renders, once the arm resolved for the customer. NO-only via
  // the experiment's targeting rule in Statsig; no market check in code.
  'fasttrack-recommendation-variant': {
    kind: 'param',
    key: 'fasttrack-recommendation-variant',
    description:
      'NO-only: every SecuredLoan recommendation (decision step + MyPage) renders the explainer card/banner (treatment) vs today\'s Fasttrack savings card or ML card (control). Primary: SecuredLoan selected (decision step + MyPage).',
    options: [
      { label: 'Control', value: { arm: 'control' } },
      { label: 'Treatment — explainer', value: { arm: 'treatment' } },
    ],
    defaultValue: { arm: 'control' },
    unit: 'userID',
  },
  // `as const satisfies` keeps `keyof typeof experimentsRegistry` a literal
  // key union — `ExperimentKey` stays exact — while still validating every
  // entry against the two allowed shapes.
} as const satisfies Record<string, ExperimentRegistryEntry>;

export type ExperimentKey = keyof typeof experimentsRegistry;

/** Runtime value of a `kind: 'param'` experiment: the union of its option values. */
export type ExperimentParams<K extends ExperimentKey> =
  (typeof experimentsRegistry)[K] extends {
    kind: 'param';
    options: readonly { value: infer V }[];
  }
    ? V
    : never;
